import { useState, useContext, useEffect, useCallback } from 'react';
import { Button, Title } from '../../../../ui';
import Tabs from '../../../../tabs/Tabs';
import SettingsIcon from '@mui/icons-material/Settings';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CircularProgress from '@mui/material/CircularProgress';
import bukApiAccessImage from '../../../../../../assets/integrations/buk-api-access.png';
import bukApiTokenImage from '../../../../../../assets/integrations/buk-api-token.png';
import bukCrearApiImage from '../../../../../../assets/integrations/crear-api.png';
import bukSeleccionarEmpresaImage from '../../../../../../assets/integrations/seleccionar-empresa.png';
import bukPermisosLecturaImage from '../../../../../../assets/integrations/permisos-lectura.png';
import logoLuca from '../../../../../../assets/logoLuca.png';
import GlobalContext from '../../../../../../context/global-context';
import { BaseHTTP } from '../../../../../../api/base.http';
import { useBukSyncStatus } from '../../../../../../hooks/useBukSyncStatus';
import BukIntegrationForm from './BukIntegrationForm';
import './BukIntegrationContent.css';

const MONTH_NAMES_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const formatPeriodLabel = (period) => {
  const [year, month] = period.split('-').map(Number);
  const monthName = MONTH_NAMES_ES[month - 1] || period;
  return `${monthName} ${year}`;
};

const BukIntegrationContent = ({ isModal = false, onClose, onCloseBackground, onFooterChange, onIntegrationComplete }) => {
  const { currentBusiness } = useContext(GlobalContext);
  const [activeTab, setActiveTab] = useState(0);
  const [currentStep, setCurrentStep] = useState(1);
  const [bukApiToken, setBukApiToken] = useState('');
  const [bukUrl, setBukUrl] = useState('');
  const [rut, setRut] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    bukUrl: false,
    bukApiToken: false,
    rut: false
  });

  const {
    syncStatus,
    progress,
    totalMonths,
    completedMonths,
    months,
    setSyncStatus,
    setProgress,
    setTotalMonths,
    setCompletedMonths,
    setMonths,
    startPolling,
    stopPolling,
    checkStatusOnce,
  } = useBukSyncStatus(currentBusiness?.id);

  const resetState = useCallback(() => {
    stopPolling();
    setCurrentStep(1);
    setBukApiToken('');
    setBukUrl('');
    setRut('');
    setError('');
    setSuccess(false);
    setSyncStatus('idle');
    setProgress(0);
    setTotalMonths(0);
    setCompletedMonths(0);
    setMonths([]);
    setFieldErrors({ bukUrl: false, bukApiToken: false, rut: false });
  }, [stopPolling, setSyncStatus, setProgress, setTotalMonths, setCompletedMonths, setMonths]);

  const handleNext = () => {
    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSkip = () => {
    setCurrentStep(activeTab === 0 ? 3 : 6);
  };

  const handleTabChange = (index) => {
    if (syncStatus === 'processing') return;
    setActiveTab(index);
    setCurrentStep(1);
    setBukApiToken('');
    setBukUrl('');
    setRut('');
    setError('');
    setSuccess(false);
    setSyncStatus('idle');
    setProgress(0);
    setTotalMonths(0);
    setCompletedMonths(0);
    setMonths([]);
    setFieldErrors({ bukUrl: false, bukApiToken: false, rut: false });
  };

  const validateFields = () => {
    const errors = {
      bukUrl: !bukUrl.trim(),
      bukApiToken: !bukApiToken.trim(),
      rut: !rut.trim()
    };
    setFieldErrors(errors);
    return !errors.bukUrl && !errors.bukApiToken && !errors.rut;
  };

  const handleSyncCompleted = useCallback(async () => {
    if (currentBusiness?.id) {
      try {
        const http = BaseHTTP.getInstance().http;
        const currentYear = new Date().getFullYear();
        await http.get(
          `v1/remuneration/business/${currentBusiness.id}/list-workers?queryFrom=01-${currentYear}&queryTo=12-${currentYear}`
        );
      } catch (fetchErr) {
        console.error('Error fetching workers list:', fetchErr);
      }
    }
    setSuccess(true);
    setLoading(false);
    if (onIntegrationComplete) {
      onIntegrationComplete();
    }
  }, [currentBusiness?.id, onIntegrationComplete]);

  const handleSyncError = useCallback((data) => {
    setError(data?.error || 'Error durante la sincronización con Buk');
    setLoading(false);
    setSyncStatus('idle');
  }, [setSyncStatus]);

  const handleConnectToBuk = async () => {
    setError('');

    if (!validateFields()) {
      setError('Por favor, completa todos los campos requeridos');
      return;
    }

    if (!currentBusiness?.id) {
      setError('No se pudo obtener la información del negocio');
      return;
    }

    setLoading(true);
    setSyncStatus('processing');
    setProgress(0);
    setCompletedMonths(0);
    setTotalMonths(0);
    setMonths([]);

    try {
      const http = BaseHTTP.getInstance().http;
      const response = await http.post(
        `v1/business/${currentBusiness.id}/integrations/buk`,
        {
          bukUrl: bukUrl.trim(),
          bukToken: bukApiToken.trim(),
          companyRut: rut.trim()
        }
      );

      if (response.data?.status === 'successful') {
        if (response.data?.syncStatus === 'processing') {
          startPolling({ onCompleted: handleSyncCompleted, onError: handleSyncError });
        } else {
          setSuccess(true);
          setLoading(false);
          setSyncStatus('completed');
        }
      } else if (response.data?.status === 'error') {
        setError(response.data?.message || 'Error al conectar con Buk. Por favor, verifica tus credenciales.');
        setLoading(false);
        setSyncStatus('idle');
      } else {
        setError('Error al conectar con Buk. Por favor, verifica tus credenciales.');
        setLoading(false);
        setSyncStatus('idle');
      }
    } catch (err) {
      if (err.response?.status === 400) {
        setError(err.response?.data?.message || 'Credenciales de Buk inválidas o no se pudo conectar con la API de Buk');
      } else {
        setError('Error al conectar con Buk. Por favor, intenta nuevamente.');
      }
      setLoading(false);
      setSyncStatus('idle');
    }
  };

  const handleFieldChange = (field, value) => {
    if (field === 'bukUrl') {
      setBukUrl(value);
      if (fieldErrors.bukUrl) {
        setFieldErrors({ ...fieldErrors, bukUrl: false });
      }
    } else if (field === 'bukApiToken') {
      setBukApiToken(value);
      if (fieldErrors.bukApiToken) {
        setFieldErrors({ ...fieldErrors, bukApiToken: false });
      }
    } else if (field === 'rut') {
      setRut(value);
      if (fieldErrors.rut) {
        setFieldErrors({ ...fieldErrors, rut: false });
      }
    }
    setError('');
  };

  useEffect(() => {
    if (success && isModal && onClose) {
      const timer = setTimeout(() => {
        resetState();
        onClose();
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [success, isModal, onClose, resetState]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  useEffect(() => {
    if (currentBusiness?.id) {
      const checkInitialStatus = async () => {
        const data = await checkStatusOnce();
        if (data?.status === 'processing') {
          setLoading(true);
          startPolling({ onCompleted: handleSyncCompleted, onError: handleSyncError });
        }
      };
      checkInitialStatus();
    }
  }, [currentBusiness?.id, checkStatusOnce, startPolling, handleSyncCompleted, handleSyncError]);

  const getFooterButtons = useCallback((forModal = false) => {
    const marginClass = forModal ? '' : 'mt-6';

    if (success) {
      return (
        <div className={`flex justify-end w-full ${marginClass}`}>
          <Button
            onClick={() => {
              resetState();
              if (onClose) onClose();
            }}
            className="text-sm bg-accent2 text-white rounded-full"
            title="Cerrar"
          />
        </div>
      );
    }

    if (syncStatus === 'processing') {
      return null;
    }

    const maxTutorialStep = activeTab === 0 ? 2 : 5;

    if (currentStep === 1) {
      return (
        <div className={`flex justify-end gap-2 w-full ${marginClass}`}>
          <Button
            onClick={handleSkip}
            className="text-sm text-gray-500 hover:text-gray-700 font-medium rounded-full hover:bg-gray-100 px-4"
            title="Omitir tutorial"
          />
          <Button
            onClick={handleNext}
            className="text-sm bg-accent2 text-white rounded-full"
            title="Siguiente"
          />
        </div>
      );
    } else if (currentStep <= maxTutorialStep) {
      return (
        <div className={`flex justify-between w-full ${marginClass}`}>
          <Button
            onClick={handleBack}
            className="text-sm bg-gray-300 text-gray-700 rounded-full"
            title="Atrás"
          />
          <div className="flex gap-2">
            <Button
              onClick={handleSkip}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium rounded-full hover:bg-gray-100 px-4"
              title="Omitir tutorial"
            />
            <Button
              onClick={handleNext}
              className="text-sm bg-accent2 text-white rounded-full"
              title="Siguiente"
            />
          </div>
        </div>
      );
    } else {
      return (
        <div className={`flex justify-between w-full ${marginClass}`}>
          <Button
            onClick={handleBack}
            disabled={loading}
            className="text-sm bg-gray-300 text-gray-700 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
            title="Atrás"
          />
          <Button
            onClick={handleConnectToBuk}
            disabled={loading}
            className="text-sm bg-accent2 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
            title={loading ? 'Conectando...' : 'Conectarse a Buk'}
          />
        </div>
      );
    }
  }, [success, syncStatus, currentStep, activeTab, loading, resetState, onClose, handleNext, handleBack, handleSkip, handleConnectToBuk]);

  const renderFooterButtons = () => getFooterButtons(false);

  useEffect(() => {
    if (isModal && onFooterChange) {
      onFooterChange(getFooterButtons(true));
    }
  }, [isModal, onFooterChange, getFooterButtons]);

  return (
    <div className="relative w-full mx-auto">
      <Title className="text-2xl mb-6">¿Cómo conectar Buk a Luca?</Title>

      {success ? (
        <div className="flex flex-col items-center justify-center py-12 space-y-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-primary mb-4">
              ¡Buk ha sido integrado correctamente!
            </div>
          </div>

          {months.length > 0 && (
            <div className="w-full max-w-md">
              <div className="text-sm font-semibold text-gray-700 mb-2 text-center">
                Trabajadores sincronizados por mes
              </div>
              <div className="bg-gray-50 rounded-lg border border-gray-200 divide-y divide-gray-200">
                {months.map((monthResult) => (
                  <div key={monthResult.period} className="px-4 py-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">{formatPeriodLabel(monthResult.period)}</span>
                      {monthResult.workers > 0 ? (
                        <span className="font-semibold text-gray-800">
                          {monthResult.workers} {monthResult.workers === 1 ? 'persona' : 'personas'}
                        </span>
                      ) : (
                        <span className="text-gray-400 italic">Sin datos desde Buk</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-right mt-2 text-xs text-gray-500">
                Total de registros sincronizados: {months.reduce((acc, m) => acc + (m.workers || 0), 0)}
              </div>
              {months.some((m) => m.workers === 0) && (
                <div className="text-right mt-1">
                  <span className="text-xs text-gray-400">
                    Contacta a soporte si quieres obtener más información
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-center mt-4">
            <img
              src={logoLuca}
              alt="Logo Luca"
              className="h-24 w-auto"
            />
          </div>
          {!isModal && renderFooterButtons()}
        </div>
      ) : syncStatus === 'processing' ? (
        <div className="flex flex-col items-center justify-center py-12 space-y-6">
          <CircularProgress size={60} className="text-primary" />
          <div className="text-center">
            {totalMonths > 0 ? (
              <>
                <div className="text-xl font-semibold text-gray-700 mb-2">
                  Sincronizando remuneraciones...
                </div>
                <div className="text-gray-600 mb-4">
                  {completedMonths}/{totalMonths} meses completados
                </div>
              </>
            ) : (
              <>
                <div className="text-xl font-semibold text-gray-700 mb-2">
                  Obteniendo datos desde Buk
                </div>
                <div className="text-gray-500 mb-4">
                  Esto puede tardar un momento...
                </div>
              </>
            )}
          </div>
          {totalMonths > 0 && (
            <div className="w-full max-w-md">
              <div className="bg-gray-200 rounded-full h-4 overflow-hidden">
                <div
                  className="bg-primary h-full rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="text-center mt-2 text-sm text-gray-600">
                {progress}% completado
              </div>
            </div>
          )}
          {months.length > 0 && (
            <div className="w-full max-w-md max-h-56 overflow-y-auto text-left space-y-1 border border-gray-200 rounded-lg p-3 bg-gray-50">
              {months.map((m) => (
                <div key={m.period} className="flex items-center justify-between text-sm py-1">
                  <span className="text-gray-700">{formatPeriodLabel(m.period)}</span>
                  {m.workers > 0 ? (
                    <span className="text-green-600 font-medium">
                      {m.workers} trabajador{m.workers === 1 ? '' : 'es'}
                    </span>
                  ) : (
                    <span className="text-amber-600 text-xs">
                      {m.message || 'Buk no tiene datos para este período'}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="text-sm text-gray-500 mt-4 mb-6">
            La sincronización continuará en segundo plano aunque cierres esta ventana.
          </div>
          {isModal && onCloseBackground && (
            <>
              <Button
                onClick={onCloseBackground}
                className="text-sm bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300"
                title="Continuar en segundo plano"
              />
              <div className="text-xs text-gray-400 mt-2">
                Puedes volver a abrir este modal para ver el progreso
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="buk-modal-tabs">
          <Tabs
            tabs={[
              {
                title: 'Plan Buk Starter',
                content: (
                  <div className="space-y-6">
                    {currentStep === 1 ? (
                      <>
                        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                          <div className="space-y-4 text-gray-700">
                            <div className="flex items-start gap-3">
                              <span className="text-xl font-bold text-primary">1.</span>
                              <div>
                                <p className="mb-2">
                                  Entra a Buk Starter con tu usuario y contraseña. Presiona la <SettingsIcon className="inline-block mx-1" /> de Ajustes y selecciona{' '}
                                  <strong>Accesos API</strong>.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-6 flex justify-center">
                          <img
                            src={bukApiAccessImage}
                            alt="Buk API Access"
                            className="max-h-80 w-auto object-contain rounded-lg shadow-md"
                          />
                        </div>
                        {!isModal && renderFooterButtons()}
                      </>
                    ) : currentStep === 2 ? (
                      <>
                        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                          <div className="space-y-4 text-gray-700">
                            <div className="flex items-start gap-3">
                              <span className="text-xl font-bold text-primary">2.</span>
                              <div className="flex-1">
                                <p className="mb-4">
                                  Presiona el <VisibilityIcon className="inline-block mx-1" /> para poder ver el token de acceso.
                                  Una vez esté visible, cópialo y vuelve aquí para continuar.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-6 flex justify-center">
                          <img
                            src={bukApiTokenImage}
                            alt="Buk API Token"
                            className="max-h-80 w-auto object-contain rounded-lg shadow-md"
                          />
                        </div>
                        {!isModal && renderFooterButtons()}
                      </>
                    ) : (
                      <>
                        <BukIntegrationForm
                          bukUrl={bukUrl}
                          bukApiToken={bukApiToken}
                          rut={rut}
                          handleFieldChange={handleFieldChange}
                          fieldErrors={fieldErrors}
                          error={error}
                          urlPlaceholder="https://miempresa.starter.buk.cl"
                          tokenPlaceholder="Token de acceso de Buk"
                          rutLabel="RUT de la empresa en Buk"
                          rutPlaceholder="12345678-9"
                          descriptionText="Ingresa la URL de tu cuenta Buk (nombreEmpresa.starter.buk.cl), el token de acceso que generaste en el paso anterior y el RUT empresa conectado a Buk:"
                        />
                        {!isModal && renderFooterButtons()}
                      </>
                    )}
                  </div>
                )
              },
              {
                title: 'Otro Plan de Buk',
                content: (
                  <div className="space-y-6">
                    {currentStep === 1 ? (
                      <>
                        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                          <div className="space-y-4 text-gray-700">
                            <div className="flex items-start gap-3">
                              <span className="text-xl font-bold text-primary">1.</span>
                              <div>
                                <p className="mb-2">
                                  Entra a Buk con tu usuario y contraseña. Presiona la <SettingsIcon className="inline-block mx-1" /> de Ajustes y selecciona{' '}
                                  <strong>Accesos API</strong>.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-6 flex justify-center">
                          <img
                            src={bukApiAccessImage}
                            alt="Buk API Access"
                            className="max-h-80 w-auto object-contain rounded-lg shadow-md"
                          />
                        </div>
                        {!isModal && renderFooterButtons()}
                      </>
                    ) : currentStep === 2 ? (
                      <>
                        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                          <div className="space-y-4 text-gray-700">
                            <div className="flex items-start gap-3">
                              <span className="text-xl font-bold text-primary">2.</span>
                              <div>
                                <p className="mb-2">
                                  En la pantalla de Accesos API, presiona el botón <strong>Crear API Key</strong> ubicado en la esquina superior derecha.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-6 flex justify-center">
                          <img
                            src={bukCrearApiImage}
                            alt="Crear API Key"
                            className="max-h-80 w-auto object-contain rounded-lg shadow-md"
                          />
                        </div>
                        {!isModal && renderFooterButtons()}
                      </>
                    ) : currentStep === 3 ? (
                      <>
                        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                          <div className="space-y-4 text-gray-700">
                            <div className="flex items-start gap-3">
                              <span className="text-xl font-bold text-primary">3.</span>
                              <div>
                                <p className="mb-2">
                                  Dale un nombre descriptivo a tu API Key (por ejemplo, <em>Api key para Luca</em>), activa la opción{' '}
                                  <strong>Limitar acceso por Empresas</strong> y selecciona la empresa que quieres conectar a Luca.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-6 flex justify-center">
                          <img
                            src={bukSeleccionarEmpresaImage}
                            alt="Seleccionar empresa"
                            className="max-h-[420px] w-auto object-contain rounded-lg shadow-md"
                          />
                        </div>
                        {!isModal && renderFooterButtons()}
                      </>
                    ) : currentStep === 4 ? (
                      <>
                        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                          <div className="space-y-4 text-gray-700">
                            <div className="flex items-start gap-3">
                              <span className="text-xl font-bold text-primary">4.</span>
                              <div>
                                <p className="mb-2">
                                  Asegúrate de que <strong>todos los permisos estén habilitados y en modo Lectura</strong>. Luego presiona <strong>Guardar</strong>.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-6 flex justify-center">
                          <img
                            src={bukPermisosLecturaImage}
                            alt="Permisos en modo lectura"
                            className="max-h-[420px] w-auto object-contain rounded-lg shadow-md"
                          />
                        </div>
                        {!isModal && renderFooterButtons()}
                      </>
                    ) : currentStep === 5 ? (
                      <>
                        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                          <div className="space-y-4 text-gray-700">
                            <div className="flex items-start gap-3">
                              <span className="text-xl font-bold text-primary">5.</span>
                              <div className="flex-1">
                                <p className="mb-4">
                                  Presiona el <VisibilityIcon className="inline-block mx-1" /> para poder ver el token de acceso,
                                  una vez esté visible, cópialo y vuelve aquí.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-6 flex justify-center">
                          <img
                            src={bukApiTokenImage}
                            alt="Buk API Token"
                            className="max-h-80 w-auto object-contain rounded-lg shadow-md"
                          />
                        </div>
                        {!isModal && renderFooterButtons()}
                      </>
                    ) : (
                      <>
                        <BukIntegrationForm
                          bukUrl={bukUrl}
                          bukApiToken={bukApiToken}
                          rut={rut}
                          handleFieldChange={handleFieldChange}
                          fieldErrors={fieldErrors}
                          error={error}
                          urlPlaceholder="Ej: https://miempresa.buk.cl"
                          tokenPlaceholder="Pega aquí tu token de API de Buk"
                          rutLabel="RUT"
                          rutPlaceholder="Ej: 12345678-9"
                          descriptionText="Completa la siguiente información:"
                        />
                        {!isModal && renderFooterButtons()}
                      </>
                    )}
                  </div>
                )
              }
            ]}
            activeIndex={activeTab}
            onTabClick={handleTabChange}
          />
        </div>
      )}
    </div>
  );
};

export default BukIntegrationContent;

